import type { ApiClient } from '@carbonwebmail/api-client'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { Field, TextAreaField } from '../../ui/Field.js'
import { useMay } from '../../ui/yourRole.js'
import { useSettings } from './useSettings.js'

/**
 * What this organisation is, and where its mail appears to come from.
 *
 * Two things on this screen are consequential and neither is obvious, so both
 * are said rather than implied.
 *
 * **The postal address goes in the footer of every campaign**, because
 * commercial email is required to carry one nearly everywhere this will be sent
 * from. Left empty the footer omits the line — it does not print a blank one —
 * so the cost of not filling it in is silent.
 *
 * **The sending identity is a starting point, not a setting that takes
 * effect.** A campaign freezes its own copy when it is written, so changing
 * these changes the next campaign and never one that has already gone out. That
 * surprises people: the obvious reading of "From address" on a settings screen
 * is that it governs everything.
 */
export function SettingsPage({ client }: { readonly client: ApiClient }) {
  const settings = useSettings({ client })
  const { state, draft, saved, problem, saving } = settings

  /*
   * A member and a viewer read these values and cannot save them.
   *
   * The fields stay visible and become read-only rather than disappearing:
   * where a campaign's From address comes from is something anybody writing
   * one needs to be able to look up, and an empty screen would not tell them.
   */
  const mayWrite = useMay('settings', 'write')

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          Who you are, and where your mail appears to come from.
        </p>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {saved ? <Alert tone="ok">Saved.</Alert> : null}

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Loading…</p>}
        onRetry={() => window.location.reload()}
      >
        {() => (
          <form
            className="max-w-2xl"
            onSubmit={(event) => {
              event.preventDefault()
              void settings.save()
            }}
          >
            <section className="rounded-card border border-line bg-surface p-5">
              <h2 className="mb-4 font-bold text-[15px] text-t1">Details</h2>

              <Field
                id="org-name"
                label="Organisation name"
                type="text"
                autoComplete="organization"
                value={draft?.name ?? ''}
                disabled={saving || draft === undefined || !mayWrite}
                hint="Shown in the footer of every campaign, so recipients can tell who is writing."
                onChange={(value) => settings.change('name', value)}
              />

              <Field
                id="org-timezone"
                label="Time zone"
                type="text"
                autoComplete="off"
                value={draft?.timezone ?? ''}
                disabled={saving || draft === undefined || !mayWrite}
                hint="An IANA zone such as Europe/London. Everything is stored in UTC and read back in this."
                onChange={(value) => settings.change('timezone', value)}
              />

              <TextAreaField
                id="org-address"
                label="Postal address"
                rows={3}
                value={draft?.postalAddress ?? ''}
                disabled={saving || draft === undefined || !mayWrite}
                hint="Printed in the footer of every campaign. Commercial email is required to carry a physical address almost everywhere — left empty, the line is simply left out."
                onChange={(value) => settings.change('postalAddress', value)}
              />
            </section>

            <section className="mt-5 rounded-card border border-line bg-surface p-5">
              <h2 className="font-bold text-[15px] text-t1">Sending identity</h2>
              {/*
                Said before the fields rather than under them. The obvious
                reading of "From address" on a settings screen is that it governs
                every send, and it does not: a campaign freezes its own copy.
              */}
              <p className="mt-1 mb-4 text-[13px] text-t2">
                What a new campaign starts from. A campaign keeps its own copy, so changing these
                never changes one that has already gone out.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  id="org-from-name"
                  label="From name"
                  type="text"
                  autoComplete="off"
                  value={draft?.fromName ?? ''}
                  disabled={saving || draft === undefined || !mayWrite}
                  onChange={(value) => settings.change('fromName', value)}
                />
                <Field
                  id="org-from-email"
                  label="From address"
                  type="email"
                  autoComplete="off"
                  value={draft?.fromEmail ?? ''}
                  disabled={saving || draft === undefined || !mayWrite}
                  onChange={(value) => settings.change('fromEmail', value)}
                />
              </div>

              <Field
                id="org-reply-to"
                optional
                label="Reply-to address"
                type="email"
                autoComplete="off"
                value={draft?.replyTo ?? ''}
                disabled={saving || draft === undefined || !mayWrite}
                hint="Where a reply goes. Left empty, replies go to the From address — which is usually a mailbox nobody reads."
                onChange={(value) => settings.change('replyTo', value)}
              />
            </section>

            {/*
              Gone rather than permanently greyed out: a Save that can never be
              pressed, with the fields above it already read-only, reads as a
              broken page rather than as somebody else's job.
            */}
            {!mayWrite ? null : (
              <button
                type="submit"
                disabled={saving || draft === undefined}
                className="mt-6 rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </form>
        )}
      </Async>
    </>
  )
}
